'use client'

// Three custom-drawn line-art icons for Section C pillars. Hand-tuned
// rather than icon-pack imports so they share the brand voice (1.4px
// olive strokes, organic geometry, subtle lime accent dot).

const OLIVE = '#5B7005'
const LIME  = '#9FAF08'

interface IconProps {
  size?: number
  className?: string
}

// Biosafety — hexagonal containment shield with checkmark + corner ticks
export function ShieldIcon({ size = 40, className }: IconProps) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
      {/* Hexagonal containment shield */}
      <path
        d="M 20 4 L 33 11 L 33 25 Q 33 32 20 36 Q 7 32 7 25 L 7 11 Z"
        fill="none"
        stroke={OLIVE}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {/* Inner perimeter (containment zone) */}
      <path
        d="M 20 9 L 28 14 L 28 24 Q 28 28 20 31 Q 12 28 12 24 L 12 14 Z"
        fill="none"
        stroke={OLIVE}
        strokeWidth="0.8"
        opacity="0.5"
      />
      {/* Checkmark */}
      <path
        d="M 14 21 L 18 25 L 26 16"
        fill="none"
        stroke={OLIVE}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Lime sentinel dot */}
      <circle cx="20" cy="6" r="1.5" fill={LIME} />
    </svg>
  )
}

// Institutional Backing — classical building with three columns + base
export function InstitutionIcon({ size = 40, className }: IconProps) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
      {/* Pediment (triangular roof) */}
      <path
        d="M 6 14 L 20 6 L 34 14"
        fill="none"
        stroke={OLIVE}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {/* Architrave */}
      <line x1="6" y1="14" x2="34" y2="14" stroke={OLIVE} strokeWidth="1.4" />
      {/* Three columns */}
      <line x1="11" y1="14" x2="11" y2="30" stroke={OLIVE} strokeWidth="1.4" />
      <line x1="20" y1="14" x2="20" y2="30" stroke={OLIVE} strokeWidth="1.4" />
      <line x1="29" y1="14" x2="29" y2="30" stroke={OLIVE} strokeWidth="1.4" />
      {/* Base / step */}
      <line x1="4"  y1="30" x2="36" y2="30" stroke={OLIVE} strokeWidth="1.4" />
      <line x1="4"  y1="34" x2="36" y2="34" stroke={OLIVE} strokeWidth="0.8" opacity="0.5" />
      {/* Lime keystone */}
      <circle cx="20" cy="11" r="1.2" fill={LIME} />
    </svg>
  )
}

// Sustainable Impact — leaf encircling a soil cross-section line
export function LeafIcon({ size = 40, className }: IconProps) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
      {/* Outer circle (ecosystem) */}
      <circle cx="20" cy="20" r="14" fill="none" stroke={OLIVE} strokeWidth="0.8" opacity="0.45" />
      {/* Leaf body — asymmetric organic curve */}
      <path
        d="M 12 28 Q 12 12 28 12 Q 28 28 12 28 Z"
        fill="none"
        stroke={OLIVE}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {/* Leaf central vein */}
      <path
        d="M 12 28 L 28 12"
        stroke={OLIVE}
        strokeWidth="1"
        strokeLinecap="round"
      />
      {/* Side veins */}
      <path d="M 16 24 L 20 22" stroke={OLIVE} strokeWidth="0.8" strokeLinecap="round" opacity="0.65" />
      <path d="M 19 21 L 22 17" stroke={OLIVE} strokeWidth="0.8" strokeLinecap="round" opacity="0.65" />
      <path d="M 22 18 L 25 14" stroke={OLIVE} strokeWidth="0.8" strokeLinecap="round" opacity="0.65" />
      {/* Lime growth dot at the tip */}
      <circle cx="28" cy="12" r="1.5" fill={LIME} />
    </svg>
  )
}
